
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Store, Key, Rocket, ArrowRight, Loader2, CheckCircle2, LogOut } from 'lucide-react';
import { SystemConfig } from '../types';
import { geocodeAddress } from '../services/maps/geocoding';
import { supabase } from '../services/supabase';
import { toast } from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  config: SystemConfig;
  onSave: (updatedConfig: SystemConfig) => Promise<void>;
}

export const OnboardingModal: React.FC<Props> = ({ isOpen, config, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    storeName: '',
    address: '',
    googleKey: ''
  });

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Sessão encerrada.");
    } catch (e) {
      toast.error("Erro ao sair.");
    }
  };

  const handleFinish = async () => {
    if (!formData.storeName || !formData.address || !formData.googleKey) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);
    try {
      const coords = await geocodeAddress(formData.address, undefined, undefined, formData.googleKey);

      if (!coords) {
        toast.error("Localização não encontrada. Verifique a chave e o endereço.");
        setLoading(false);
        return;
      }

      const updatedConfig: SystemConfig = {
        ...config,
        storeName: formData.storeName,
        businessAddress: formData.address,
        businessLat: coords.lat,
        businessLng: coords.lng,
        googleMapsApiKey: formData.googleKey
      };

      await onSave(updatedConfig);
      toast.success("Bem-vindo ao S.A.L. Rota!");
    } catch (error) {
      toast.error("Erro ao salvar configuração.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-sal-gunmetal/95 backdrop-blur-xl">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-sal-gunmetal-light w-full max-w-lg rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden"
      >
        <div className="p-8 border-b border-white/5 text-center relative">
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Sair desta conta"
            className="absolute top-6 right-6 p-2 text-slate-500 hover:text-rose-400 transition-colors"
            title="Sair desta conta"
          >
            <LogOut className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 bg-sal-cyber-teal/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-sal-cyber-teal/20 text-sal-cyber-teal">
            <Rocket className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Prepare seu Cérebro Logístico</h2>
          <p className="text-slate-500 text-sm font-bold uppercase tracking-widest mt-2">Configuração Inicial Obrigatória</p>
        </div>

        <div className="p-8 space-y-6">
          <div className="space-y-4">
            <div className="group">
              <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.2em] mb-2 block ml-1">Nome da Operação</label>
              <div className="relative">
                <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input name="storeName" autoComplete="organization" type="text" value={formData.storeName} onChange={e => setFormData({ ...formData, storeName: e.target.value })} placeholder="Ex: Pizzaria do João" className="w-full bg-black/40 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-slate-600 focus:border-sal-cyber-teal/50 outline-none transition-all" />
              </div>
            </div>
            <div className="group">
              <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.2em] mb-2 block ml-1">Endereço da Base (Hub)</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input name="address" autoComplete="street-address" type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Rua, Número, Bairro, Cidade - Estado" className="w-full bg-black/40 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-slate-600 focus:border-sal-cyber-teal/50 outline-none transition-all" />
              </div>
            </div>
            <div className="group">
              <label className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.2em] mb-2 block ml-1">Google Maps API Key (BYOK)</label>
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input name="googleKey" autoComplete="off" type="password" value={formData.googleKey} onChange={e => setFormData({ ...formData, googleKey: e.target.value })} placeholder="AIza..." className="w-full bg-black/40 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-slate-600 focus:border-sal-cyber-teal/50 outline-none transition-all font-mono" />
              </div>
            </div>
          </div>
          <div className="bg-sal-cyber-teal/5 rounded-2xl p-4 border border-sal-cyber-teal/10">
            <p className="text-xs text-slate-400 font-medium leading-relaxed">Suas credenciais são salvas apenas no seu projeto Supabase pessoal. O S.A.L. Rota é um sistema BYOK.</p>
          </div>
        </div>

        <div className="p-8 bg-sal-gunmetal border-t border-white/5">
          <button onClick={handleFinish} disabled={loading} className="w-full bg-sal-cyber-teal text-sal-gunmetal py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-sm flex items-center justify-center gap-3 shadow-xl hover:brightness-110 active:scale-95 transition-all disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processando...</> : <><CheckCircle2 className="w-4 h-4" /> Ativar Sistema</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
};
